<template>
  <div class="pa-8 px-22">
    <header class="mb-8">
      <v-card-title
        class="headline font-weight-bold mb-1 pa-0 letter-spacing-compact"
      >
        {{ user.id ? 'Edit' : 'Add New' }}
        {{ user.role.convertToReadable().capitalize() }}
      </v-card-title>
      <hr />
    </header>
    <div>
      <validation-observer ref="validator">
        <v-form @submit.prevent="submit()">
          <ValidationProvider
            name="first_name"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="first_name"
              :error-messages="errors"
              v-model="request.first_name"
              label="First Name"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            name="last_name"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="last_name"
              :error-messages="errors"
              v-model="request.last_name"
              label="Last Name"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            v-if="request.hasOwnProperty('email')"
            name="email"
            rules="required|email"
            v-slot="{ errors }"
          >
            <v-text-field
              name="email"
              :error-messages="errors"
              v-model="request.email"
              label="Email"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            name="phone_number"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="phone_number"
              :error-messages="errors"
              v-model="request.phone_number"
              label="Phone Number"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            v-if="request.hasOwnProperty('bank_name')"
            name="bank_name"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="bank_name"
              :error-messages="errors"
              v-model="request.bank_name"
              label="Bank"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            v-if="request.hasOwnProperty('bank_account_number')"
            name="bank_account_number"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="bank_account_number"
              :error-messages="errors"
              v-model="request.bank_account_number"
              label="Account Number"
              required
            />
          </ValidationProvider>

          <CountryField
            v-if="request.hasOwnProperty('country_code')"
            name="country"
            rules="required"
            label="Country"
            item-value="alpha2Code"
            v-model="request.country_code"
          >
          </CountryField>
          <ValidationProvider
            v-if="request.hasOwnProperty('years_of_diving')"
            name="diving_years"
            rules="required|integer"
            v-slot="{ errors }"
          >
            <v-text-field
              name="years_of_diving"
              :error-messages="errors"
              v-model="request.years_of_diving"
              label="Years of diving"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            v-if="request.hasOwnProperty('no_of_dives')"
            name="diving_number"
            rules="required|integer"
            v-slot="{ errors }"
          >
            <v-text-field
              name="no_of_dives"
              :error-messages="errors"
              v-model="request.no_of_dives"
              label="Number of dives"
              required
            />
          </ValidationProvider>
          <ValidationProvider
            v-if="request.hasOwnProperty('business')"
            name="company_name"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              name="business_name"
              :error-messages="errors"
              v-model="request.business.name"
              label="Company Name"
            />
          </ValidationProvider>

          <ValidationProvider
            v-if="request.hasOwnProperty('business')"
            name="business_website"
            v-slot="{ errors }"
          >
            <v-text-field
              name="business_website"
              website="business_website"
              :error-messages="errors"
              v-model="request.business.website"
              label="Company Website"
            />
          </ValidationProvider>

          <LocationField
            v-if="request.hasOwnProperty('business')"
            name="business_location"
            v-model="request.business.location"
            :required="true"
          />
          <div v-if="request.hasOwnProperty('business')">
            <label class="d-block mt-6 mb-8 grey--text">
              Type of trips (Select all that apply)
            </label>
            <v-chip
              v-for="(trip, index) in trips"
              :key="index"
              :class="
                request.business.trip_ids.includes(trip.id)
                  ? 'black white--text'
                  : ''
              "
              :outlined="!request.business.trip_ids.includes(trip.id)"
              class="mr-6 mb-6 px-7 py-4 body-1 border-radius-5 h-40 black"
              @click="toggleTrip(trip.id)"
              height="40px"
            >
              {{ trip.name }}
            </v-chip>

            <validation-provider
              class="mt-12"
              name="trips"
              rules="required"
              v-slot="{ errors }"
            >
              <input
                type="hidden"
                name="trips[]"
                v-model="request.business.trip_ids"
              />
              <div
                v-if="errors.length && request.business.trip_ids.length < 1"
                class="v-messages theme--light error--text"
                role="alert"
              >
                <div class="v-messages__wrapper">
                  <div class="v-messages__message">
                    Please select at least one
                  </div>
                </div>
              </div>
            </validation-provider>
          </div>
          <v-btn
            v-if="isDrawer"
            large
            color="white"
            class="elevation-1 mb-5 font-weight-bold"
            @click="cancel()"
            block
          >
            Cancel
          </v-btn>
          <v-btn
            large
            block
            color="primary"
            class="elevation-1 font-weight-bold"
            @click.prevent="submit()"
          >
            Done
          </v-btn>
        </v-form>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import Request from '@request/UserRequest';
import userCrud from '@mixins/userCrud';

export default {
  name: 'Create',
  mixins: [userCrud],
  metaInfo: {
    titleTemplate: '%s - Manage Community Manager',
  },
  components: {
    CountryField: () => import('@components/utilities/CountryField'),
  },
  props: {
    isDrawer: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      request: new Request(this.$refs, {
        ...this.user,
      }),
    };
  },
  methods: {
    async submit() {
      if (await this.$refs.validator.validate()) {
        let response = await this.request.submit();
        if (response) {
          if (this.request.id) {
            Object.assign(this.user, response.data);
          }
          this.request.reset();
          this.backToList(response.data);
        }
      }
    },
    cancel() {
      this.$refs.validator.reset();
      this.backToList();
    },
    backToList(user) {
      this.$emit('exit', user);
    },
  },
  mounted() {
    if (this.user && this.user.role === 'tourism-operator') {
      this.getTrips();
    }
  },
};
</script>

<style scoped></style>
